import { CoreLayoutView, CoreTextView, ORIENTATION_ENUM } from '@core/widgets';
import { useBehavior } from '@libs/hooks';
import { TWidgetProps } from '@libs/types';
import { numberFormat, significantDecimalPlaces } from '@libs/utils';
import React from 'react';
import { TLimitBarData } from '../../types/data';
import { TLimitBarOption } from '../../types/options';
import { TextStyle } from '../../types/styles';
import styles from './limitBar.module.css';

const getMaximumFractionDigits = (amount: number) => {
    const v = significantDecimalPlaces(amount);
    return v > 2 ? 2 : v;
};

export const LimitBar: React.FC<TWidgetProps<TLimitBarData, TLimitBarOption>> = ({ widget }) => {
    const { currencyIso, currentAmount = 0, maxAmount = 0 } = widget.data?.value ?? {};
    const { limitTitle, currentSubtitle, maxSubtitle } = widget.data?.messages ?? {};
    const { margin, isError } = widget.options ?? {};
    const iso = currencyIso?.toUpperCase?.();
    const { isHidden } = useBehavior(widget);

    const mutedColor = 'var(--color-grey_72_lc)';
    const purpleColor = 'var(--color-purple_100)';
    const pinkColor = 'var(--color-pink_100)';

    const leftAmount = maxAmount - currentAmount;
    const progress = (currentAmount / maxAmount) * 100;
    const progressWidth = progress <= 0 ? '8px' : `${progress}%`;

    const spentAmount = numberFormat(currentAmount, getMaximumFractionDigits(currentAmount)) as string;
    const remainingAmount = numberFormat(leftAmount, getMaximumFractionDigits(leftAmount)) as string;

    const parentStyleNames = [widget.options?.style].filter(Boolean) as string[];

    return (
        <CoreLayoutView
            uid={widget.uid}
            styleName={widget.options?.style}
            parentStyleNames={parentStyleNames}
            isHide={isHidden}
            styleProps={{ orientation: ORIENTATION_ENUM.VERTICAL, lineX: 'stretch', margin }}
        >
            <CoreTextView
                styleName="limitsTitle"
                parentStyleNames={parentStyleNames}
                text={limitTitle}
                styleProps={{ ...TextStyle.main, main: 'var(--color-black)' }}
            />
            {/* Progress bar */}
            <CoreLayoutView
                isError={isError}
                styleName="progressWrapper"
                parentStyleNames={parentStyleNames}
                styleProps={{
                    orientation: ORIENTATION_ENUM.HORIZONTAL,
                    lineX: 'start',
                    height: 8,
                    borderRadius: 6,
                    overflow: 'hidden',
                    margin: { bottom: 16, top: 24 },
                    backgroundColor: 'var(--color-grey_8_lc)',
                }}
            >
                <CoreLayoutView
                    uid={widget.uid}
                    isError={isError}
                    className={styles.progressBarLine}
                    styleName="progressView"
                    parentStyleNames={parentStyleNames}
                    style={{
                        width: progressWidth,
                    }}
                    styleProps={{ height: 8, borderRadius: 6, backgroundColor: purpleColor, error: { backgroundColor: pinkColor } }}
                />
            </CoreLayoutView>
            {/* Amounts + subtitles row */}
            <CoreLayoutView
                uid={`amounts_${widget.uid}`}
                parentStyleNames={parentStyleNames}
                styleProps={{ orientation: ORIENTATION_ENUM.HORIZONTAL, lineX: 'space_between', lineY: 'start' }}
                style={{ alignItems: 'flex-end', alignSelf: 'flex-end', margin: 'auto' }}
            >
                {/* Spent column — color = accent */}
                <CoreLayoutView parentStyleNames={parentStyleNames} styleProps={{ orientation: ORIENTATION_ENUM.VERTICAL, lineX: 'start', gap: 5 }}>
                    <CoreLayoutView styleProps={{ orientation: ORIENTATION_ENUM.HORIZONTAL, lineX: 'start', lineY: 'center', gap: 4 }}>
                        <CoreTextView
                            uid={widget.uid}
                            styleName="limitsSpentAmount"
                            parentStyleNames={parentStyleNames}
                            as="span"
                            isError={isError}
                            text={spentAmount}
                            styleProps={{
                                ...TextStyle.header,
                                main: purpleColor,
                                error: {
                                    main: pinkColor,
                                },
                            }}
                        />
                        <CoreTextView
                            styleName="limitsSpentCurrency"
                            parentStyleNames={parentStyleNames}
                            as="span"
                            text={iso}
                            isError={isError}
                            styleProps={{
                                ...TextStyle.header,
                                main: purpleColor,
                                error: {
                                    main: pinkColor,
                                },
                            }}
                        />
                    </CoreLayoutView>
                    <CoreTextView
                        styleName="limitsSpentSubtitle"
                        parentStyleNames={parentStyleNames}
                        text={currentSubtitle}
                        isError={isError}
                        styleProps={{
                            ...TextStyle.secondary,
                            main: purpleColor,
                            textAlign: 'start',
                            error: {
                                main: pinkColor,
                            },
                        }}
                    />
                </CoreLayoutView>

                {/* Remaining column */}
                <CoreLayoutView parentStyleNames={parentStyleNames} styleProps={{ orientation: ORIENTATION_ENUM.VERTICAL, lineX: 'end', gap: 5 }}>
                    <CoreLayoutView styleProps={{ orientation: ORIENTATION_ENUM.HORIZONTAL, lineX: 'end', lineY: 'center', gap: 4 }}>
                        <CoreTextView
                            styleName="limitsRemainingAmount"
                            parentStyleNames={parentStyleNames}
                            as="span"
                            text={remainingAmount}
                            isError={isError}
                            styleProps={{ ...TextStyle.main, main: mutedColor }}
                        />
                        <CoreTextView
                            styleName="limitsRemainingCurrency"
                            parentStyleNames={parentStyleNames}
                            as="span"
                            text={iso}
                            isError={isError}
                            styleProps={{
                                ...TextStyle.main,
                                main: mutedColor,
                            }}
                        />
                    </CoreLayoutView>
                    <CoreTextView
                        styleName="limitsRemainingSubtitle"
                        parentStyleNames={parentStyleNames}
                        text={maxSubtitle}
                        isError={isError}
                        styleProps={{ ...TextStyle.secondary, main: mutedColor, textAlign: 'end' }}
                    />
                </CoreLayoutView>
            </CoreLayoutView>
        </CoreLayoutView>
    );
};
